import { useState } from 'react';
import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { useSeoMeta } from '@unhead/react';
import { Loader2, Plus, RadioTower, X } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useAppContext } from '@/hooks/useAppContext';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import { usePublisherRelays, useUpdatePublisherList, type PublisherRelay } from '@/hooks/usePublisherRelays';
import { normalizeRelayUrl } from '@/lib/nip51';
import { sanitizeUrl } from '@/lib/sanitizeUrl';

/** Compact avatar + name linking to a profile, used for a relay's recommenders. */
function AuthorChip({ pubkey }: { pubkey: string }) {
  const author = useAuthor(pubkey);
  const name = author.data?.metadata?.name ?? `${pubkey.slice(0, 8)}…`;
  const picture = sanitizeUrl(author.data?.metadata?.picture);

  return (
    <Link
      to={`/${nip19.npubEncode(pubkey)}`}
      className="inline-flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
    >
      <Avatar className="size-5 shrink-0">
        <AvatarImage src={picture} alt="" />
        <AvatarFallback>{name.charAt(0).toUpperCase()}</AvatarFallback>
      </Avatar>
      <span className="max-w-28 truncate">{name}</span>
    </Link>
  );
}

interface RelayRowProps {
  relay: PublisherRelay;
  inMyNetwork: boolean;
  isMine: boolean;
  onAddToNetwork: (url: string) => void;
  onRemove: (url: string) => void;
  removing: boolean;
}

function RelayRow({ relay, inMyNetwork, isMine, onAddToNetwork, onRemove, removing }: RelayRowProps) {
  const shown = relay.addedBy.slice(0, 2);
  const extra = relay.addedBy.length - shown.length;

  return (
    <li className="flex items-center gap-3 py-3">
      <RadioTower className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="truncate font-mono text-sm break-all">{relay.url.replace(/^wss:\/\//, '')}</p>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
          {shown.map(({ pubkey }) => (
            <AuthorChip key={pubkey} pubkey={pubkey} />
          ))}
          {extra > 0 && <span className="text-xs text-muted-foreground">+{extra} more</span>}
        </div>
      </div>
      {inMyNetwork ? (
        <Badge variant="secondary" className="shrink-0 text-xs" title="Your client already reads from this relay">
          in your network
        </Badge>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="xs"
          onClick={() => onAddToNetwork(relay.url)}
          title="Add this relay to your network (read-only — enable writes in Network settings to publish there too)"
          className="shrink-0 gap-1"
        >
          <Plus className="size-3" />
          Add to network
        </Button>
      )}
      {isMine && (
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          disabled={removing}
          onClick={() => onRemove(relay.url)}
          title="Remove from your publisher list"
          className="shrink-0 text-muted-foreground hover:text-destructive"
        >
          {removing ? <Loader2 className="size-3 animate-spin" /> : <X className="size-3" />}
        </Button>
      )}
    </li>
  );
}

/**
 * The "Publishers list" (`/publishers`) — a community-maintained registry
 * of relays that host (and want to be indexed for) NIP-54 wiki articles.
 *
 * The list is the union of everyone's NIP-51 kind 10102 ("good wiki
 * relays") list: nobody owns it, anyone can add a relay, and each entry
 * shows who vouched for it. Adding/removing edits your own single
 * replaceable list event, so "the community edits the list" falls out of
 * plain NIP-51 semantics — no moderators, no custom kinds.
 */
export function PublishersPage() {
  const { user } = useCurrentUser();
  const { config, updateConfig } = useAppContext();
  const { mutate: publishEvent } = useNostrPublish();
  const { toast } = useToast();
  const { data: relays, isLoading } = usePublisherRelays();
  const update = useUpdatePublisherList();
  const [url, setUrl] = useState('');

  useSeoMeta({
    title: 'Publishers list — Wikistar',
    description:
      'A community-maintained list of relays that host Nostr wiki articles — add your relay so readers and indexers know where to look.',
  });

  // The URLs on the current user's own kind 10102 list (derived from the
  // aggregate — no separate fetch needed).
  const myUrls = new Set(
    (relays ?? [])
      .filter((r) => r.addedBy.some((a) => a.pubkey === user?.pubkey))
      .map((r) => r.url),
  );

  // URLs the user's client is already configured to read/write (NIP-65).
  const networkUrls = new Set(config.relayMetadata.relays.map((r) => normalizeRelayUrl(r.url) ?? r.url));

  const handleAdd = async () => {
    const normalized = normalizeRelayUrl(url);
    if (!normalized) {
      toast({ title: 'Invalid relay URL', description: 'Enter a relay URL like wss://relay.example.com', variant: 'destructive' });
      return;
    }
    if (myUrls.has(normalized)) {
      toast({ title: 'Already listed', description: 'That relay is already on your publisher list.' });
      setUrl('');
      return;
    }
    try {
      await update.mutateAsync([...myUrls, normalized]);
      setUrl('');
      toast({ title: 'Relay added', description: `${normalized.replace(/^wss:\/\//, '')} is now on the publishers list.` });
    } catch (error) {
      toast({ title: 'Failed to add relay', description: error instanceof Error ? error.message : undefined, variant: 'destructive' });
    }
  };

  const handleRemove = async (target: string) => {
    try {
      await update.mutateAsync([...myUrls].filter((u) => u !== target));
      toast({ title: 'Relay removed', description: `${target.replace(/^wss:\/\//, '')} was removed from your list.` });
    } catch (error) {
      toast({ title: 'Failed to remove relay', description: error instanceof Error ? error.message : undefined, variant: 'destructive' });
    }
  };

  /**
   * Adds a listed relay to the user's NIP-65 network as read-only: the
   * button's promise is "read wiki content from this relay", and silently
   * also publishing everything the user writes to a publicly-listed relay
   * would be a surprise. Writes can be enabled in Network settings.
   * Follows RelayListManager's save pattern: persist locally, and publish
   * the updated kind 10002 when logged in.
   */
  const handleAddToNetwork = (target: string) => {
    if (networkUrls.has(target)) return;
    const newRelays = [...config.relayMetadata.relays, { url: target, read: true, write: false }];
    updateConfig((current) => ({
      ...current,
      relayMetadata: { relays: newRelays, updatedAt: Math.floor(Date.now() / 1000) },
    }));
    if (user) {
      const tags = newRelays
        .map((r) => (r.read && r.write ? ['r', r.url] : r.read ? ['r', r.url, 'read'] : ['r', r.url, 'write']))
        .filter((t): t is string[] => Boolean(t));
      publishEvent({ kind: 10002, content: '', tags });
    }
    toast({
      title: 'Added to your network',
      description: `Now reading from ${target.replace(/^wss:\/\//, '')} — enable writes in Network settings to publish there too.`,
    });
  };

  return (
    <Layout>
      <div className="mx-auto max-w-3xl px-4 py-8">
        <div className="mb-2 flex items-center gap-2">
          <RadioTower className="size-6 text-primary" />
          <h1 className="text-2xl font-bold tracking-tight">Publishers list</h1>
        </div>
        <p className="mb-6 text-sm text-muted-foreground">
          Relays that have opted in to hosting — and being indexed for — wiki articles. The list is
          the union of everyone's public "good wiki relays" list (NIP-51, kind 10102): nobody owns
          it, anyone can add to it, and every entry shows who vouched for it. Operate a relay? Add
          it below so readers and indexers know where to find wiki content.
        </p>

        {user ? (
          <Card className="mb-6">
            <CardContent className="space-y-3 pt-6">
              <Label htmlFor="publisher-relay-url">Add a relay</Label>
              <div className="flex gap-2">
                <Input
                  id="publisher-relay-url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                  placeholder="wss://relay.example.com"
                  className="font-mono text-sm"
                />
                <Button type="button" onClick={handleAdd} disabled={!url.trim() || update.isPending} className="shrink-0">
                  {update.isPending && update.variables?.includes(normalizeRelayUrl(url) ?? '') ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Plus className="size-4" />
                  )}
                  Add relay
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                This edits your own public kind 10102 list — one replaceable event, yours alone to
                change. Anyone browsing this page sees you vouched for the relay.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="mb-6 border-dashed">
            <CardContent className="py-8 px-8 text-center">
              <p className="mx-auto max-w-sm text-muted-foreground">
                Log in to add a relay to the publishers list.
              </p>
            </CardContent>
          </Card>
        )}

        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 py-2">
                <Skeleton className="size-4 rounded" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="ml-auto size-5 rounded-full" />
              </div>
            ))}
          </div>
        ) : relays && relays.length > 0 ? (
          <ol className="divide-y divide-border">
            {relays.map((relay) => (
              <RelayRow
                key={relay.url}
                relay={relay}
                inMyNetwork={networkUrls.has(relay.url)}
                isMine={myUrls.has(relay.url)}
                onAddToNetwork={handleAddToNetwork}
                onRemove={handleRemove}
                removing={update.isPending && !update.variables?.includes(relay.url)}
              />
            ))}
          </ol>
        ) : (
          <Card className="border-dashed">
            <CardContent className="py-12 px-8 text-center">
              <p className="mx-auto max-w-sm text-muted-foreground">
                No publisher relays yet. If you operate a relay that hosts wiki articles, be the
                first to add it.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
